// Error types and interfaces for comprehensive error handling

export interface AppError {
  code: string;
  message: string;
  userMessage: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  timestamp: string;
  context?: Record<string, any>;
  retryable?: boolean;
  suggestions?: string[];
}

export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  RATE_LIMIT = 'rate_limit',
  UNKNOWN = 'unknown',
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export interface NetworkError extends AppError {
  category: ErrorCategory.NETWORK;
  statusCode?: number;
  endpoint?: string;
}

export interface AuthenticationError extends AppError {
  category: ErrorCategory.AUTHENTICATION;
  authCode?: string;
}

export interface ValidationError extends AppError {
  category: ErrorCategory.VALIDATION;
  field?: string;
  validationRule?: string;
}

export interface ServiceUnavailableError extends AppError {
  category: ErrorCategory.SERVICE_UNAVAILABLE;
  serviceName: string;
  fallbackAvailable?: boolean;
}

// Error recovery strategies
export interface ErrorRecoveryStrategy {
  type: 'retry' | 'fallback' | 'redirect' | 'manual';
  action?: () => Promise<void> | void;
  label?: string;
  description?: string;
}

// Error context for better debugging and user assistance
export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  timestamp: string;
  feature?: string;
  action?: string;
  additionalData?: Record<string, any>;
}

// Error reporting interface
export interface ErrorReport {
  error: AppError;
  context: ErrorContext;
  stackTrace?: string;
  userFeedback?: string;
}